// src/components/giftcards/GiftCardPdf.js
import React from 'react';
import PropTypes from 'prop-types';
import {
  PDFDownloadLink,
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
} from '@react-pdf/renderer';
import { Button } from '@mui/material';
import logo from '../assets/reserve-logo-white.png';

const WEBSITE_URL = 'marrakech-reserve.com';

const styles = StyleSheet.create({
  page: {
    position: 'relative',
    padding: 0,
    fontFamily: 'Helvetica',
    backgroundColor: 'transparent',
  },
  cardContainer: {
    flex: 1,
    backgroundColor: '#006241',
    padding: 12,
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  logo: {
    width: 240,
    height: 80,
    objectFit: 'contain',
    marginBottom: 8,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  title: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: 'bold',
  },
  amount: {
    color: '#ffffff',
    fontSize: 28,
    fontWeight: 'extrabold',
  },
  message: {
    color: '#ffffff',
    fontSize: 12,
    textAlign: 'center',
    marginVertical: 8,
  },
  codeBox: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderRadius: 4,
    padding: 4,
  },
  codeText: {
    color: '#ffffff',
    fontSize: 16,
    fontFamily: 'Courier',
    textAlign: 'center',
    letterSpacing: 1,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontSize: 8,
    color: '#ffffff',
    marginTop: 8,
  },
  website: {
    color: '#ffffff',
    fontSize: 8,
    textAlign: 'center',
    marginTop: 4,
  },
});

const GiftCardDocument = ({ code, amount, issuedAt, toName, fromName }) => (
  <Document>
    <Page size="A6" orientation="landscape" style={styles.page}>
      <View style={styles.cardContainer}>
        <View style={styles.header}>
          <Image src={logo} style={styles.logo} />
          <View>
            <Text style={styles.title}>GIFT CARD</Text>
            <Text style={styles.amount}>€{amount}</Text>
          </View>
        </View>

        <Text style={styles.message}>Enjoy your gift!</Text>

        <View style={styles.codeBox}>
          <Text style={styles.codeText}>
            {code.match(/.{1,4}/g).join(' ')}
          </Text>
        </View>

        <View style={styles.footer}>
          <Text>{toName ? `To: ${toName}` : ''}</Text>
          <Text>{fromName ? `From: ${fromName}` : ''}</Text>
          <Text>{new Date(issuedAt).toLocaleDateString()}</Text>
        </View>

        <Text style={styles.website}>Visit {WEBSITE_URL} to redeem</Text>
      </View>
    </Page>
  </Document>
);

export default function GiftCardPdf({
  code,
  amount,
  issuedAt,
  toName,
  fromName,
}) {
  return (
    <PDFDownloadLink
      document={
        <GiftCardDocument
          code={code}
          amount={amount}
          issuedAt={issuedAt}
          toName={toName}
          fromName={fromName}
        />
      }
      fileName={`giftcard_${code}.pdf`}
      style={{ textDecoration: 'none', flex: 1 }}
    >
      {({ loading }) => (
        <Button
          variant="contained"
          color="primary"
          fullWidth
          disabled={loading}
          sx={{ py: 1.5 }}
        >
          {loading ? 'Generating…' : 'Download PDF'}
        </Button>
      )}
    </PDFDownloadLink>
  );
}

GiftCardPdf.propTypes = {
  code: PropTypes.string.isRequired,
  amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  issuedAt: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  toName: PropTypes.string,
  fromName: PropTypes.string,
};

GiftCardPdf.defaultProps = {
  toName: '',
  fromName: '',
};
